import { addDays, format } from 'date-fns';
import { useState } from 'react';
import { useCustomEventEffect } from './useCustomEventEffect';

function dispatch<T = any>(identifier: string, detail?: T) {
  document.dispatchEvent(new CustomEvent<T>(identifier, { detail }));
}

interface IInsightsFilterChange {
  includeObfuscated?: boolean;
  selectedChannels?: string[];
  selectedLocations?: string[];
  selectedDepartments?: string[];
  selectedDivisions?: string[];
  selectedStructureCustomGroupOnes?: string[];
  selectedJobs?: string[];
  selectedJobGroups?: string[];
  selectedJobStatuses?: string[];
  selectedHiringManagers?: string[];
  selectedRecruitmentManagers?: string[];
  endDate?: string;
  startDate?: string;
  selectedCandidateStages: string[];
}

export type IInsightsFilterChangeEvent = CustomEvent<IInsightsFilterChange>;
const filterChangeTarget = (identifier: string) => `insights:${identifier}:filterChange`;

export function dispatchInsightsFilterChangeEvent(identifier: string, selection: IInsightsFilterChange) {
  return dispatch<IInsightsFilterChange>(filterChangeTarget(identifier), selection);
}

export function useInsightsFilterChangeEventEffect(
  identifier: string,
  callback: (event: IInsightsFilterChangeEvent) => any,
) {
  return useCustomEventEffect(filterChangeTarget(identifier), callback);
}

export function useInsightFilters(
  identifier: string,
  queryCallback: (...args: any) => any,
  filterChangeCallback?: (event: IInsightsFilterChangeEvent) => any,
  dateFilterKey: 'created_at' | 'start_at' = 'created_at',
) {
  const [periodStart, setPeriodStart] = useState<string>();
  const [periodEnd, setPeriodEnd] = useState<string>();

  useInsightsFilterChangeEventEffect(identifier, handleFilterChange);

  function handleFilterChange(event: IInsightsFilterChangeEvent) {
    const {
      includeObfuscated,
      selectedChannels,
      selectedLocations,
      selectedDivisions,
      selectedDepartments,
      selectedStructureCustomGroupOnes,
      selectedJobs,
      selectedJobGroups,
      selectedJobStatuses,
      selectedHiringManagers,
      selectedRecruitmentManagers,
      endDate,
      startDate,
      selectedCandidateStages,
    } = event.detail;
    const formattedStartDate = startDate ? format(startDate, 'YYYY[-]MM[-]DD') : null;
    const formattedEndDate = endDate ? format(addDays(endDate, 1), 'YYYY[-]MM[-]DD') : null;
    setPeriodStart(startDate);
    setPeriodEnd(endDate);

    if (filterChangeCallback) {
      filterChangeCallback(event);
      return;
    }

    queryCallback({
      channel: { eq: selectedChannels },
      [dateFilterKey]: { gte: formattedStartDate, lte: formattedEndDate },
      department_id: { eq: selectedDepartments },
      division_id: { eq: selectedDivisions },
      include_obfuscated: { eq: includeObfuscated },
      job_group_id: { eq: selectedJobGroups },
      job_id: { eq: selectedJobs },
      location_id: { eq: selectedLocations },
      hiring_manager_user_id: { eq: selectedHiringManagers },
      recruitment_manager_user_id: { eq: selectedRecruitmentManagers },
      structure_custom_group_one_id: { eq: selectedStructureCustomGroupOnes },
      candidate_stages: { eq: selectedCandidateStages },
      job_status: { eq: selectedJobStatuses },
    });
  }

  const humanizeTimePeriod = `between ${format(periodStart, 'MMMM Do YYYY')} and ${format(periodEnd, 'MMMM Do YYYY')}`;

  return {
    humanizeTimePeriod,
    periodEnd,
    periodStart,
  };
}

export default useInsightFilters;
